import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Link, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TopAppBar from '../layout/TopAppBar';
import { PageContainer, SurfaceCard } from '../ui/ConvivialComponents';
import { LEGAL_DOCUMENTS, acceptLegalDocuments } from '../../services/firebaseData';
import { useAuth } from '../../context/AuthContext';
import { getAnalyticsConsent, saveAnalyticsConsent } from '../../services/analytics';

export default function LegalConsentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [analyticsAccepted, setAnalyticsAccepted] = useState(() => getAnalyticsConsent() === 'granted');
  const [newsletter, setNewsletter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleContinue = async () => {
    if (!accepted || !userId) return;
    setSaving(true);
    setError('');
    try {
      await acceptLegalDocuments(userId, newsletter);
      // Apply the optional choice only after the required documents are saved.
      saveAnalyticsConsent(analyticsAccepted);
      const redirectTo = location.state?.redirectTo;
      navigate(typeof redirectTo === 'string' && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
        ? redirectTo : '/index', { replace: true });
    } catch (err) {
      console.error('Unable to save legal consents:', err);
      setError('Non siamo riusciti a salvare le tue preferenze. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopAppBar title="ConvivialApp.it" showAvatar={false} />
      <PageContainer maxWidth="620px" withBottomNav={false} sx={{ pt: 'calc(var(--cv-top-bar-height) + 32px)', minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
        <SurfaceCard sx={{ width: '100%', maxWidth: 560 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h1" sx={{ color: 'primary.main', fontSize: 30, mb: 1 }}>Prima di continuare</Typography>
              <Typography color="text.secondary">Per usare Convivial, leggi e accetta i documenti indicati.</Typography>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />}
              label={<Typography variant="body2">Ho letto e accetto i <Link href={LEGAL_DOCUMENTS.termsUrl} target="_blank" rel="noreferrer">Termini e condizioni</Link> e confermo di aver preso visione della <Link href={LEGAL_DOCUMENTS.privacyUrl} target="_blank" rel="noreferrer">Privacy Policy</Link>. *</Typography>}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox checked={analyticsAccepted} onChange={(event) => setAnalyticsAccepted(event.target.checked)} />}
              label={<Typography variant="body2">Consento l’uso di Google Analytics per misurare schermate, clic e tempi di utilizzo, senza inviare il contenuto dei moduli o dei messaggi. <Typography component="span" color="text.secondary">(modificabile in Privacy e comunicazioni)</Typography></Typography>}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox checked={newsletter} onChange={(event) => setNewsletter(event.target.checked)} />}
              label={<Typography variant="body2">Desidero ricevere newsletter e comunicazioni di marketing da Convivial <Typography component="span" color="text.secondary">(modificabile in qualsiasi momento)</Typography>.</Typography>}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
            <Button variant="contained" size="large" disabled={!accepted || saving} onClick={handleContinue} sx={{ minHeight: 44, borderRadius: 2.5, fontWeight: 600 }}>
              {saving ? 'Salvataggio…' : 'Continua'}
            </Button>
            <Typography variant="caption" color="text.secondary">Puoi consultare anche la <Link href={LEGAL_DOCUMENTS.cookieUrl} target="_blank" rel="noreferrer">Cookie Policy</Link>.</Typography>
          </Stack>
        </SurfaceCard>
      </PageContainer>
    </>
  );
}
